import OpenSeadragon from "openseadragon";
import { fromEvent, Subject } from "rxjs";
import CoordinateConversion from "../tools/CoordinateConversion.js";
import Overlay from "./Overlay.js";
import Tooltip from "./Tooltip.js";
import { debounceTime } from "rxjs/operators";

const _debug = false;

const _defaultConfig = {
    className: "imageview-overlay",
    tooltipClassName: "imageview-overlay-tooltip",
    selectionClassName: "selected",
    highlightClassName: "highlight",
    highlightOnHover: false,
    onlyRenderHighlighted: false,
    tooltip: {
        enabled: true,
        className: "highlight-tooltip",
        onHover: true
    }
}

class OverlayGroup {

    constructor(viewer, config) {
        this.config = jQuery.extend(true, {}, _defaultConfig, config);
        this.viewer = viewer;
        this.overlays = [];
        this.tooltips = new Map();
        this.onHighlight = new Subject();
        this.offHighlight = new Subject();
        this.selectedOverlay = undefined;
    }

    addToViewer(overlaySources) {
        if(_debug)console.log("addToViewer", overlaySources);
        return overlaySources.map( source => {
            const image = source.target ? this.viewer.getImageById(source.target) : this.openseadragon?.world?.getItemAt(0);
            if(_debug)console.log("load overlay ", source, " in ", image);
            if(image) {
                const imageRect = new OpenSeadragon.Rect(source.coordinates[0], source.coordinates[1], source.coordinates[2], source.coordinates[3]);
                const bounds = imageRect.times(1/image.source.width).times(image.scale ?? 1).translate(image.getBounds().getTopLeft());
                return this.addViewportRect(bounds, source.id, source.tooltip);
            }
        })
        .filter(o => o != undefined);
    }

    addViewportRect(rect, id, tooltipText) {
        const overlay = new Overlay(rect, {style: {className: this.config.className}}, id);
        if(_debug)console.log("created overlay: ", overlay, rect, id);
        this.overlays.push(overlay);
        if(!this.config.onlyRenderHighlighted) {
            overlay.draw(this.viewer);
        }
        if(this.config.highlightOnHover && !this.config.onlyRenderHighlighted) {
            if(_debug)console.log("init highlight on hover for ", overlay);
            overlay.element.addEventListener("mouseenter", e => this.setHighlight(overlay.id))
            overlay.element.addEventListener("mouseleave", e => this.removeHighlight(overlay.id))
        } else if(this.config.highlightOnHover) {
            fromEvent(this.viewer.element, "mousemove")
            .pipe(debounceTime(5))
            .subscribe(e => this.highlightOverlayAtLocation(e));
        }

        if(this.config.tooltip.enabled && tooltipText) {
            const tooltip = new Tooltip(overlay, tooltipText, this.viewer, this.config.tooltip);
            this.tooltips.set(overlay.id, tooltip);
        }
        return overlay;
    }

    highlightOverlayAtLocation(event) {
        const eventInCanvas = new OpenSeadragon.Point(event.clientX - this.viewer.element.getBoundingClientRect().left, event.clientY - this.viewer.element.getBoundingClientRect().top);
        const eventInViewport =  CoordinateConversion.convertPointFromCanvasToImage(eventInCanvas, this.viewer.openseadragon);
        if(this.highlightedOverlay) {
            const canvasRect = this.highlightedOverlay.bounds;
            if(!canvasRect?.containsPoint(eventInViewport)) {
                this.removeHighlight(this.highlightedOverlay);
                this.highlightedOverlay = undefined;
            }
        } else {
            this.highlightedOverlay = this.overlays.find(overlay => overlay.bounds?.containsPoint(eventInViewport));
            if(this.highlightedOverlay) {
                this.setHighlight(this.highlightedOverlay);
            }
        }
    }

    removeFromViewer(overlays) {
        if(overlays) {
            if(!Array.isArray(overlays)) {
                overlays = [overlays];
            }
            overlays.forEach(o => o.remove());
            this.overlays = this.overlays.filter(o => !overlays.includes(o));
        } else {
            this.overlays.forEach(o => o.remove());
            this.overlays = [];
        }
    }

    getOverlayById(id) {
        return this.overlays?.find(o => o.id === id);
    }

    showTooltip(id, tooltip) {
        const tooltipElement = this.tooltips.get(id);
        //if(!tooltipElement) {
            const overlay = this.getOverlayById(id);
            tooltipElement = this.createTooltip(overlay, tooltip);
        //}
        if(tooltipElement) {
            tooltipElement.classList.add('show');
        }
    }

    createTooltip(overlay, tooltip) {
        if(_debug)console.log("show tooltip ", overlay, tooltip ?? overlay.tooltip);
        if(overlay) {

            const windowBounds = this.viewer.openseadragon.viewport.viewportToViewerElementRectangle(overlay.bounds);
            if(_debug)console.log("overlay bounds ", overlay.bounds, windowBounds, overlay.element.getBoundingClientRect());
            const overlayRect = windowBounds;// overlay.element.getBoundingClientRect();
            const tooltipElement = document.createElement("div");
            tooltipElement.id = overlay.element.id + "_tooltip";
            tooltipElement.className = this.config.tooltipClassName;
            // Position tooltip above the element
            tooltipElement.style.left = overlayRect.x  + 'px';
            tooltipElement.style.top = overlayRect.y +  tooltipElement.offsetHeight - 10 + 'px';
            tooltipElement.innerHTML = tooltip ?? overlay.tooltip;
            this.viewer.element.appendChild(tooltipElement);
            this.tooltips.set(overlay.element.id, tooltipElement);
            return tooltipElement;
        }
    }

    hideTooltip(id) {
        const tooltipElement = this.tooltips.get(id);
        if(tooltipElement) {
            tooltipElement.classList.remove("show");
        }
    }

    selectOverlay(overlayOrId) {
        if(overlayOrId) {
            const overlay = overlayOrId instanceof ImageView.Overlay ? overlayOrId : this.getOverlayById(overlayOrId);
            if(overlay) {
                if(this.selectedOverlay === overlay) {
                    return overlay;
                }
                if(this.selectedOverlay) {
                    this.deselectOverlay(this.selectedOverlay);
                }
                this.selectedOverlay = overlay;
                if(!overlay.osOverlay) {
                    overlay.draw(this.viewer);
                }
                overlay?.element?.classList.add(this.config.selectionClassName);
                return overlay;
            }
        }
    }

    deselectOverlay() {
        if(this.selectedOverlay) {
            const deselectedOverlay = this.selectOverlay;
            if(this.config.onlyRenderHighlighted) {
                this.selectedOverlay.remove();
            } else {
                    this.selectedOverlay.element?.classList.remove(this.config.selectionClassName);
            }
            this.selectedOverlay = undefined;
            return deselectedOverlay;
        }
    }



    highlightOverlay(overlay, highlight) {
        if(highlight) {
            this.setHighlight(overlay);
        } else {
            this.removeHighlight(overlay);
        }
    }

    removeHighlight(id, hideTooltip) { 
        if(_debug)console.log("remove highlight of overlay ", id);
        if(id) {
            const overlay = id instanceof ImageView.Overlay ? id : this.getOverlayById(id);
            if(overlay && overlay == this.selectedOverlay) {
                overlay.element?.classList.remove(this.config.highlightClassName)
                return;
            }
            overlay?.element?.classList.remove(this.config.highlightClassName);
            if(this.config.onlyRenderHighlighted) {
                overlay.remove();
            }
            if(hideTooltip) {
                this.tooltips.get(overlay?.id)?.hide();
            }
            this.offHighlight.next(overlay);
        } else {
            this.overlays.forEach(o => {
                if(hideTooltip) {
                    this.tooltips.get(o.id)?.hide();
                }
                o.element?.classList.remove(this.config.highlightClassName)
                if(this.config.onlyRenderHighlighted) {
                    overlay.remove();
                }
            })
        }
    }

    setHighlight(id, showTooltip) {
        if(_debug)console.log("highlight overlay ", id);
        if(id) {
            const overlay = id instanceof ImageView.Overlay ? id : this.getOverlayById(id);
            if(overlay && overlay == this.selectedOverlay) {
                return;
            }
            if(!overlay.osOverlay) {
                overlay.draw(this.viewer);
            }
            overlay?.element?.classList.add(this.config.highlightClassName);
            if(showTooltip) {
                this.tooltips.get(overlay?.id)?.show();
            }
            this.onHighlight.next(overlay);
        } else {
            if(_debug)console.log("add highlight ", this.overlays, this.config.highlightClassName)
            this.overlays.forEach(o => {
                if(!o.osOverlay) {
                    o.draw(this.viewer);
                }
                if(showTooltip) {
                    this.tooltips.get(o.id)?.show();
                }
                o.element?.classList.add(this.config.highlightClassName)
            }); 
        }
    }

}

export default OverlayGroup;